import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export type AgenteStato = "attivo" | "in_arrivo" | "sospeso";

export interface AgenteMetriche {
  email_7gg: number;
  in_errore: number;
  costo_ai_7gg_usd: number;
}

export interface Agente {
  slug: string;
  nome: string;
  ruolo: string;
  modulo: string;
  canale_primario: string;
  stato: AgenteStato;
  referente: string | null;
  colore: string;
  canali: string[];
  metriche?: AgenteMetriche;
  ultima_attivita_il?: string | null;
}

export function useAdminAgenti() {
  return useQuery({
    queryKey: ["admin", "agenti"],
    queryFn: () => apiFetch<{ agenti: Agente[] }>("/api/v1/admin/agenti"),
  });
}

export function useAdminAgente(slug: string | undefined) {
  return useQuery({
    queryKey: ["admin", "agente", slug],
    queryFn: () => apiFetch<Agente>(`/api/v1/admin/agenti/${slug}`),
    enabled: !!slug,
  });
}


export interface StrumentoRiepilogo {
  tool: string;
  usi: number;
  errori: number;
  durata_media_ms: number | null;
  durata_max_ms: number | null;
  ultimo_uso: string | null;
}

export interface StrumentoUso {
  id: string;
  tool: string;
  argomenti: Record<string, unknown> | null;
  esito: "ok" | "errore";
  errore: string | null;
  durata_ms: number | null;
  risposta_bytes: number | null;
  conversazione_id: string | null;
  utente_id: string | null;
  /** Valorizzato quando sarà un agente a chiedere a un altro agente. */
  richiesto_da_agente: string | null;
  quando: string;
}

/**
 * Il registro degli strumenti usati da un agente: serve al monitoraggio e
 * all'affinamento — davanti a una risposta sbagliata dice se il problema è il
 * prompt o lo strumento (richiesta di Enrico, 16-08-2026).
 */
export function useAdminAgenteStrumenti(
  slug: string | undefined,
  opts: { tool?: string | null; soloErrori?: boolean } = {}
) {
  const q = new URLSearchParams();
  if (opts.tool) q.set("tool", opts.tool);
  if (opts.soloErrori) q.set("solo_errori", "true");
  return useQuery({
    queryKey: ["admin", "agenti", slug, "strumenti", opts.tool ?? null, !!opts.soloErrori],
    queryFn: () =>
      apiFetch<{ riepilogo: StrumentoRiepilogo[]; usi: StrumentoUso[] }>(
        `/api/v1/admin/agenti/${slug}/strumenti?${q.toString()}`
      ),
    enabled: !!slug,
    staleTime: 15_000,
  });
}
